import _ from 'lodash';
import _clone from 'lodash/clone';
import _groupBy from 'lodash/groupBy';
import _sortBy from 'lodash/sortBy';
import _sumBy from 'lodash/sumBy';
import classnames from 'classnames';
import moment from 'moment';
import React, { Component, useState } from 'react';
import numeral from 'numeral';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Query } from '@apollo/react-components';
import { gql } from '@apollo/client';
import SpinnerError from '$components/library/SpinnerError';
import TooltipContent from './TooltipContent';

import styles from './styles.scss';

const COLORS = [
  '#00db37',
  '#00da62',
  '#00d78c',
  '#00d2b8',
  '#00cde3',
  '#00c6ff',
  '#00beff',
  '#00b4ff',
  '#00a6ff',
  '#0094ff',
  '#007bff',
  '#7457ff',
  '#c300ff',
];

const colorMap = {
  'amc-8': COLORS[0],
  'amc-10': COLORS[1],
  'amc-12': COLORS[2],
  'finra-bundle-sie-s7': COLORS[3],
  'finra-everything': COLORS[4],
  'finra-series-6': COLORS[0],
  'finra-series-63': COLORS[5],
  'finra-series-65': COLORS[6],
  'finra-series-66': COLORS[7],
  'finra-series-7': COLORS[8],
  'finra-sie': COLORS[9],
  gre: COLORS[10],
  toefl: COLORS[11],
  'usmle-step-1': COLORS[12],
  trend: '#b7b7b7',
};

const PARTNER_CHARGES = gql`
  query PartnerCharges($input: PartnerChargesInput!) {
    partnerCharges(input: $input) {
      partnerCharges {
        uuid
        userUuid
        product {
          uuid
          slug
        }
        achievableShare
        achievableShareRefunded
        amount
        amountRefunded
        authorShare
        authorShareRefunded
        createdAt
        otherShare
        otherShareRefunded
        refundedAt
        txFee
      }
    }
  }
`;

const getEventsFromCharges = ({ charges }) => {
  const events = [];
  charges.forEach((charge) => {
    const {
      uuid,
      achievableShare,
      achievableShareRefunded,
      amount,
      amountRefunded,
      authorShare,
      authorShareRefunded,
      createdAt,
      otherShare,
      otherShareRefunded,
      product,
      refundedAt,
      txFee,
    } = charge;

    const event = {
      uuid,
      product,
      amount,
      txFee,
      authorShare,
      achievableShare,
      otherShare,
      type: 'purchase',
      eventAt: createdAt,
    };
    events.push(event);

    if (refundedAt) {
      const refundEvent = {
        uuid,
        product,
        amount: -1 * amountRefunded,
        authorShare: -1 * authorShareRefunded,
        achievableShare: -1 * achievableShareRefunded,
        otherShare: -1 * otherShareRefunded,
        type: 'refund',
        eventAt: refundedAt,
      };
      events.push(refundEvent);
    }
  });
  return events;
};

const EventTile = ({ label, value }) => {
  return (
    <div className={styles.eventTile}>
      <div className={styles.eventTileLabel}>{label}</div>
      <div className={styles.eventTileValue}>{value}</div>
    </div>
  );
};

const formatAmount = (amount) => {
  if (!amount) {
    return '-';
  }

  return numeral(amount).format('$0,00.00');
};

const EventRow = (props) => {
  const { event } = props;
  const {
    uuid: chargeUuid,
    product: { slug },
    amount,
    txFee,
    achievableShare,
    authorShare,
    otherShare,
    type,
    eventAt,
  } = event;

  return (
    <div className={styles.eventRow}>
      <EventTile label="Uuid" value={chargeUuid.substring(0, 8)} />
      <EventTile label="Product" value={slug} />
      <EventTile label="Type" value={type} />
      <EventTile label="Amount" value={formatAmount(amount)} />
      <EventTile label="Tx fee" value={formatAmount(txFee / 100)} />
      <EventTile label="Author share" value={formatAmount(authorShare)} />
      <EventTile label="Achievable share" value={formatAmount(achievableShare)} />
      <EventTile label="Other share" value={formatAmount(otherShare)} />
      <EventTile label="Occurred at" value={moment(eventAt).format('M/D/YY h:mma')} />
    </div>
  );
};

const MonthStats = (props) => {
  const [detailsVisible, setDetailsVisible] = useState(false);
  const { monthBucket } = props;
  const { key, events, purchasesTotal, refundsTotal, total } = monthBucket;
  const title = moment(`${key}-01`).format('MMMM YYYY');
  const sortedEvents = _sortBy(events, 'eventAt'); // eslint-disable-line no-unused-vars

  const formattedTotal = numeral(total).format('$0,0.00');
  const formattedCharges = numeral(purchasesTotal).format('$0,0.00');
  const formattedRefunds = numeral(refundsTotal).format('$0,0.00');

  const showHideIconClassName = detailsVisible ? 'fad fa-minus' : 'fad fa-plus';
  const showHideToggle = () => {
    setDetailsVisible(!detailsVisible);
  };

  return (
    <div className={styles.monthStatsMonth}>
      <div className={styles.monthStatsHeader}>
        <div className={styles.monthStatsHeaderMonth}>{title}</div>
        <div className={styles.monthStatsHeaderInfo}>
          <div className={styles.monthStatsHeaderSubtotals}>
            {formattedCharges} {formattedRefunds} ={' '}
          </div>
          <div className={styles.monthStatsHeaderTotal}>{formattedTotal}</div>
          <i
            className={classnames(styles.showHideToggle, showHideIconClassName)}
            onClick={showHideToggle}
          />
        </div>
      </div>
      {detailsVisible && (
        <div className={styles.monthEvents}>
          {sortedEvents.map((event) => {
            const { uuid: chargeUuid } = event;
            const eventKey =
              event.type === 'purchase'
                ? `${chargeUuid}-purchase`
                : `${chargeUuid}-refund`;
            return <EventRow key={eventKey} event={event} />;
          })}
        </div>
      )}
    </div>
  );
};

const StatsCard = (props) => {
  const { items, title } = props;

  return (
    <div className={styles.cardLayout}>
      <div className={styles.cardTitle}>{title}</div>
      <div className={styles.cardContent}>
        <div className={classnames(styles.cardStat, styles.cardMainStat)}>
          <div className={styles.text}>{items[0].text}</div>
          <div className={styles.subText}>{items[0].subText}</div>
        </div>
        <div className={styles.subStats}>
          <div className={classnames(styles.cardStat, styles.cardSubStat)}>
            <div className={styles.text}>{items[1].text}</div>
            <div className={styles.subText}>{items[1].subText}</div>
          </div>
          <div className={classnames(styles.cardStat, styles.cardSubStat)}>
            <div className={styles.text}>{items[2].text}</div>
            <div className={styles.subText}>{items[2].subText}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AUTHOR_SLUGS = ['basic-wisdom', 'graceusmle', 'stellar', 'wescarroll'];
const getPartnerType = ({ partnerSlug }) => {
  if (partnerSlug === 'achievable') {
    return 'achievable';
  }

  if (_.includes(AUTHOR_SLUGS, partnerSlug)) {
    return 'author';
  }

  return 'other';
};

class PartnerCharges extends Component {
  render() {
    const { match } = this.props;
    const { params } = match;
    const { partnerSlug } = params;

    const partnerType = getPartnerType({ partnerSlug });
    const shareKey = `${partnerType}Share`;

    return (
      <div className={styles.layout}>
        <Query
          query={PARTNER_CHARGES}
          variables={{ input: { partnerKey: partnerSlug, partnerType } }}
        >
          {({ loading, error, data }) => {
            if (loading || error) {
              return <SpinnerError error={error} />;
            }

            const {
              partnerCharges: { partnerCharges },
            } = data;

            const events = getEventsFromCharges({ charges: partnerCharges });

            const eventsByMonth = _groupBy(events, (event) => {
              const { eventAt } = event;
              const month = moment(eventAt).utc().format('YYYY-MM');
              return month;
            });

            const sortedMonths = Object.keys(eventsByMonth).sort();
            const lastMonthKey = moment().utc().format('YYYY-MM');

            const monthKeys = (() => {
              if (sortedMonths.length === 0) {
                return [lastMonthKey];
              }

              const firstMonth = sortedMonths[0];

              const dateStart = moment(`${firstMonth}-01`);
              const dateEnd = moment(`${lastMonthKey}-01`);
              const interim = dateStart.clone();
              const timeValues = [];

              while (dateEnd > interim || interim.format('M') === dateEnd.format('M')) {
                timeValues.push(interim.format('YYYY-MM'));
                interim.add(1, 'month');
              }

              return timeValues;
            })();

            let lifetimeTotal = 0;
            let lifetimePurchases = 0;
            let lifetimeRefunds = 0;

            const monthBucketsAscending = monthKeys.map((monthKey) => {
              const eventsForMonth = eventsByMonth[monthKey] || [];
              const purchasesTotal = _sumBy(
                eventsForMonth.filter((event) => event.type === 'purchase'),
                (event) => event[shareKey]
              );
              const refundsTotal = _sumBy(
                eventsForMonth.filter((event) => event.type === 'refund'),
                (event) => event[shareKey]
              );

              const totalAmc8 = _sumBy(
                eventsForMonth.filter((event) => event.product.slug === 'amc-8'),
                (event) => event[shareKey]
              );
              const totalAmc10 = _sumBy(
                eventsForMonth.filter((event) => event.product.slug === 'amc-10'),
                (event) => event[shareKey]
              );
              const totalAmc12 = _sumBy(
                eventsForMonth.filter((event) => event.product.slug === 'amc-12'),
                (event) => event[shareKey]
              );
              const totalFinraBundleSieS7 = _sumBy(
                eventsForMonth.filter(
                  (event) => event.product.slug === 'finra-bundle-sie-s7'
                ),
                (event) => event[shareKey]
              );
              const totalFinraEverything = _sumBy(
                eventsForMonth.filter(
                  (event) => event.product.slug === 'finra-everything'
                ),
                (event) => event[shareKey]
              );
              const totalFinraSeries6 = _sumBy(
                eventsForMonth.filter(
                  (event) => event.product.slug === 'finra-series-6'
                ),
                (event) => event[shareKey]
              );
              const totalFinraSeries63 = _sumBy(
                eventsForMonth.filter(
                  (event) => event.product.slug === 'finra-series-63'
                ),
                (event) => event[shareKey]
              );
              const totalFinraSeries65 = _sumBy(
                eventsForMonth.filter(
                  (event) => event.product.slug === 'finra-series-65'
                ),
                (event) => event[shareKey]
              );
              const totalFinraSeries66 = _sumBy(
                eventsForMonth.filter(
                  (event) => event.product.slug === 'finra-series-66'
                ),
                (event) => event[shareKey]
              );
              const totalFinraSeries7 = _sumBy(
                eventsForMonth.filter((event) => event.product.slug === 'finra-series-7'),
                (event) => event[shareKey]
              );
              const totalFinraSie = _sumBy(
                eventsForMonth.filter((event) => event.product.slug === 'finra-sie'),
                (event) => event[shareKey]
              );
              const totalGre = _sumBy(
                eventsForMonth.filter((event) => event.product.slug === 'gre'),
                (event) => event[shareKey]
              );
              const totalUsmleStep1 = _sumBy(
                eventsForMonth.filter((event) => event.product.slug === 'usmle-step-1'),
                (event) => event[shareKey]
              );

              const total = purchasesTotal + refundsTotal;
              lifetimeTotal += total;
              lifetimePurchases += purchasesTotal;
              lifetimeRefunds += refundsTotal;
              return {
                key: monthKey,
                events: eventsForMonth,
                total,
                totalAmc8,
                totalAmc10,
                totalAmc12,
                totalFinraBundleSieS7,
                totalFinraEverything,
                totalFinraSeries6,
                totalFinraSeries63,
                totalFinraSeries65,
                totalFinraSeries66,
                totalFinraSeries7,
                totalFinraSie,
                totalGre,
                totalUsmleStep1,
                purchasesTotal,
                refundsTotal,
              };
            });

            const hasAmc8 = _.some(monthBucketsAscending, (d) => d.totalAmc8);
            const hasAmc10 = _.some(monthBucketsAscending, (d) => d.totalAmc10);
            const hasAmc12 = _.some(monthBucketsAscending, (d) => d.totalAmc12);
            const hasFinraBundleSieS7 = _.some(
              monthBucketsAscending,
              (d) => d.totalFinraBundleSieS7
            );
            const hasFinraEverything = _.some(
              monthBucketsAscending,
              (d) => d.totalFinraEverything
            );
            const hasFinraSeries6 = _.some(
              monthBucketsAscending,
              (d) => d.totalFinraSeries6
            );
            const hasFinraSeries63 = _.some(
              monthBucketsAscending,
              (d) => d.totalFinraSeries63
            );
            const hasFinraSeries65 = _.some(
              monthBucketsAscending,
              (d) => d.totalFinraSeries65
            );
            const hasFinraSeries66 = _.some(
              monthBucketsAscending,
              (d) => d.totalFinraSeries66
            );
            const hasFinraSeries7 = _.some(
              monthBucketsAscending,
              (d) => d.totalFinraSeries7
            );
            const hasFinraSie = _.some(monthBucketsAscending, (d) => d.totalFinraSie);
            const hasGre = _.some(monthBucketsAscending, (d) => d.totalGre);
            const hasUsmleStep1 = _.some(monthBucketsAscending, (d) => d.totalUsmleStep1);

            const monthBuckets = _clone(monthBucketsAscending).reverse();

            const startOfMonth = moment().startOf('month').unix();
            const endOfMonth = moment().endOf('month').unix();
            const nowOfMonth = moment().unix();

            const finalTrendPointObj =
              monthBucketsAscending[monthBucketsAscending.length - 1];
            if (finalTrendPointObj) {
              finalTrendPointObj.trend =
                finalTrendPointObj.total *
                  ((endOfMonth - startOfMonth) / (nowOfMonth - startOfMonth)) -
                finalTrendPointObj.total;
            }

            const penultimateTrendPointObj =
              monthBucketsAscending[monthBucketsAscending.length - 2];
            if (penultimateTrendPointObj) {
              penultimateTrendPointObj.trend = 0;
            }

            return (
              <>
                <div className={styles.cards}>
                  <StatsCard
                    title="Lifetime"
                    items={[
                      {
                        text: numeral(lifetimeTotal).format('$0,0.00'),
                        subText: 'Net',
                      },
                      {
                        text: numeral(lifetimePurchases).format('$0,0.00'),
                        subText: 'Purchases',
                      },
                      {
                        text: numeral(-1 * lifetimeRefunds).format('$0,0.00'),
                        subText: 'Refunds',
                      },
                    ]}
                  />
                </div>
                <center>
                  <div className={styles.monthsChart}>
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={monthBucketsAscending}>
                        <XAxis
                          dataKey="key"
                          tickFormatter={(val) => {
                            return moment(`${val}-01`).format('MMM');
                          }}
                        />
                        <YAxis
                          tickFormatter={(val) => {
                            return numeral(val).format('$0,0');
                          }}
                        />
                        <Tooltip content={<TooltipContent colorMap={colorMap} />} />
                        {hasAmc8 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalAmc8"
                            fill={colorMap['amc-8']}
                          />
                        )}
                        {hasAmc10 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalAmc10"
                            fill={colorMap['amc-10']}
                          />
                        )}
                        {hasAmc12 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalAmc12"
                            fill={colorMap['amc-12']}
                          />
                        )}
                        {hasFinraBundleSieS7 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalFinraBundleSieS7"
                            fill={colorMap['finra-bundle-sie-s7']}
                          />
                        )}
                        {hasFinraEverything && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalFinraEverything"
                            fill={colorMap['finra-everything']}
                          />
                        )}
                        {hasFinraSeries6 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalFinraSeries6"
                            fill={colorMap['finra-series-6']}
                          />
                        )}
                        {hasFinraSeries63 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalFinraSeries63"
                            fill={colorMap['finra-series-63']}
                          />
                        )}
                        {hasFinraSeries65 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalFinraSeries65"
                            fill={colorMap['finra-series-65']}
                          />
                        )}
                        {hasFinraSeries66 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalFinraSeries66"
                            fill={colorMap['finra-series-66']}
                          />
                        )}
                        {hasFinraSeries7 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalFinraSeries7"
                            fill={colorMap['finra-series-7']}
                          />
                        )}
                        {hasFinraSie && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalFinraSie"
                            fill={colorMap['finra-sie']}
                          />
                        )}
                        {hasGre && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalGre"
                            fill={colorMap.gre}
                          />
                        )}
                        {hasUsmleStep1 && (
                          <Area
                            stackId="1"
                            type="monotone"
                            dataKey="totalUsmleStep1"
                            fill={colorMap['usmle-step-1']}
                          />
                        )}
                        <Area
                          stackId="1"
                          type="monotone"
                          dataKey="trend"
                          fill={colorMap.trend}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className={styles.monthStats}>
                    {monthBuckets.map((monthBucket) => (
                      <MonthStats key={monthBucket.key} monthBucket={monthBucket} />
                    ))}
                  </div>
                </center>
              </>
            );
          }}
        </Query>
      </div>
    );
  }
}

export default PartnerCharges;
